// Janela de Prey no estilo do Tibia: até 3 espaços; escolher criatura da lista, trocar a lista, trocar o bônus
// com prey card, escolher qualquer criatura (5 cards) e liberar o 3º espaço na loja.
// Visual "caçador": couro, verde-musgo, âmbar e marcas de garra (CSS em client/css-parts/prey.css, prefixo .py-).
import { useState } from 'preact/hooks';
import { PREY_BONUS_NAMES, PREY_CARD_BONUS, PREY_CARD_PICK, PREY_DURATION_S, preySlots, preyValue, type PreyBonus, type PreyState } from '../../../../shared/src/game/prey';
import type { Outfit } from '../../../../shared/src/protocol';
import { Portrait } from './BattleList';

const IMG = '/assets/interface/images';
const BONUS_IMG: Record<PreyBonus, string> = { damage: 'damage-boost', defense: 'damage-reduction', xp: 'improved-xp', loot: 'improved-loot' };
const BONUS_SHORT: Record<PreyBonus, string> = { damage: 'Dano', defense: 'Defesa', xp: 'Experiência', loot: 'Loot' };
const BONUS_ORDER: PreyBonus[] = ['damage', 'defense', 'xp', 'loot'];
const ROMAN = ['I', 'II', 'III'];
const hm = (s: number) => `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
const HELP_KEY = 'nx-help-prey';
const seen = () => { try { return localStorage.getItem(HELP_KEY) === '1'; } catch { return false; } };
const markSeen = () => { try { localStorage.setItem(HELP_KEY, '1'); } catch { /* sem storage */ } };

export type PreySend = (op: 'list' | 'pick' | 'bonus' | 'any' | 'cancel', slot: number, monster?: string) => void;

export function PreyWindow(props: { st: PreyState; rerollGold: number; all: string[]; outfitOf: (name: string) => Outfit | undefined; coins: number; send: PreySend; onBuy: (offer: string) => void }) {
  const st = props.st;
  const open = preySlots(st);
  const active = st.slots.slice(0, open).filter((s) => s.monster && s.bonus && (s.left ?? 0) > 0);
  const nothingYet = !st.slots.some((s) => s.monster);
  const [help, setHelp] = useState<boolean | null>(null);
  const showHelp = help ?? (nothingYet && !seen());
  const toggleHelp = () => { markSeen(); setHelp(!showHelp); };
  const freeSoon = st.slots.slice(0, open).filter((s) => !s.monster).map((s) => s.freeAt);
  const freeNow = freeSoon.some((t) => Date.now() >= t);

  return (
    <div class="py">
      {/* cabeçalho: a marca do caçador */}
      <div class="py-head">
        <div class="py-claws"><i /><i /><i /></div>
        <div class="py-emblem" title="Prey"><img src={`${IMG}/icon-prey-preycreature.png`} alt="" /></div>
        <div class="py-head-txt">
          <div class="py-title">Prey <button class="py-help" onClick={toggleHelp} title="Como funciona">?</button></div>
          <div class="py-sub">Marque sua presa. Cace com vantagem.</div>
          <div class="py-tags">
            <span>Bônus por {Math.round(PREY_DURATION_S / 3600)} horas de caça</span>
            <span>Lista nova grátis a cada 20 horas</span>
            <span>Até 3 presas ao mesmo tempo</span>
          </div>
        </div>
      </div>

      {/* números que importam */}
      <div class="py-stats">
        <div class={'py-stat' + (st.cards ? ' amber' : ' empty')} title="Prey cards: trocar o bônus custa 1; escolher qualquer criatura custa 5.">
          <small>Prey cards</small>
          <b><img src={`${IMG}/prey-wildcards-icon.png`} alt="" />{st.cards}</b>
          <button class="tb-button py-buy" disabled={props.coins < 25} onClick={() => props.onBuy('preycards5')}>+5 cards · 25 coins</button>
        </div>
        <div class={'py-stat' + (active.length ? ' moss' : '')} title="Espaços com bônus correndo agora">
          <small>Presas ativas</small>
          <b>{active.length}<em>/{open}</em></b>
          <span class="py-pips">{[0, 1, 2].map((i) => <i key={i} class={i >= open ? 'lock' : i < active.length ? 'on' : ''} />)}</span>
        </div>
        <div class="py-stat" title="Bônus ativos agora">
          <small>Bônus correndo</small>
          <span class="py-chips">
            {active.length ? active.map((s, i) => (
              <span key={i} class="py-chip"><img src={`${IMG}/prey-bonus-${BONUS_IMG[s.bonus!.type]}-small.png`} alt="" />+{preyValue(s.bonus!.type, s.bonus!.grade)}%</span>
            )) : <span class="py-none">nenhum</span>}
          </span>
        </div>
        <div class={'py-stat' + (freeNow ? ' moss' : '')} title="Trocar a lista de criaturas">
          <small>Lista nova</small>
          <b class="py-sm">{!freeSoon.length ? '—' : freeNow ? 'Grátis' : hm(Math.ceil((Math.min(...freeSoon) - Date.now()) / 1000))}</b>
          <span>{!freeSoon.length ? 'todos os espaços caçando' : freeNow ? 'em algum espaço livre' : `ou ${props.rerollGold.toLocaleString('pt-BR')} gp`}</span>
        </div>
      </div>

      {showHelp && (
        <div class="py-steps">
          <div><i>🎯</i><b>1. Escolha a presa</b><small>Cada espaço mostra 9 criaturas. Clique em uma e confirme. Não dá para repetir a mesma criatura em dois espaços.</small></div>
          <div><i>⭐</i><b>2. Ganhe um bônus</b><small>Sai um bônus aleatório com grau de 1 a 10 estrelas: quanto mais estrelas, maior a porcentagem.</small></div>
          <div><i>⏳</i><b>3. Cace</b><small>O bônus vale por 2 horas de caça contra essa criatura. O tempo só corre enquanto você caça.</small></div>
          <div><i>🃏</i><b>4. Prey cards</b><small>1 card sorteia outro bônus (e o tempo volta a 2 h). 5 cards deixam escolher qualquer criatura das hunts.</small></div>
          <div class="py-table">
            <b>Bônus possíveis (1★ → 10★)</b>
            {BONUS_ORDER.map((t) => (
              <span key={t}><img src={`${IMG}/prey-bonus-${BONUS_IMG[t]}-small.png`} alt="" />{PREY_BONUS_NAMES[t]}<em>{preyValue(t, 1)}% → {preyValue(t, 10)}%</em></span>
            ))}
          </div>
        </div>
      )}

      <div class="py-slots">
        {[0, 1, 2].map((i) => i < open
          ? <Slot key={i} i={i} {...props} />
          : (
            <div key={i} class="py-card py-locked">
              <div class="py-card-top"><span class="py-num">{ROMAN[i]}</span><b>Espaço trancado</b></div>
              <div class="py-lockart"><img src={`${IMG}/prey-noprey.png`} alt="" /><span>🔒</span></div>
              <p class="py-lockinfo">3º espaço de prey: mais uma criatura com bônus ao mesmo tempo, <b>para sempre</b>.</p>
              <div class="py-price"><b>250</b> Noxara Coins</div>
              <button class="py-unlock" disabled={props.coins < 250} title="250 Noxara Coins" onClick={() => props.onBuy('prey3')}><img src={`${IMG}/prey-unlock-permanently.png`} alt="Liberar para sempre" /></button>
              {props.coins < 250 && <small class="py-why">Você tem {props.coins.toLocaleString('pt-BR')} coins.</small>}
            </div>
          ))}
      </div>
    </div>
  );
}

function Slot(props: { i: number; st: PreyState; rerollGold: number; all: string[]; outfitOf: (name: string) => Outfit | undefined; send: PreySend }) {
  const sl = props.st.slots[props.i];
  const [sel, setSel] = useState<string | null>(null);
  const [any, setAny] = useState(false);
  const [anyName, setAnyName] = useState('');
  if (!sl) return <div class="py-card py-empty"><div class="py-card-top"><span class="py-num">{ROMAN[props.i]}</span><b>Carregando...</b></div></div>;
  const used = props.st.slots.filter((x, k) => k !== props.i).map((x) => x.monster);

  if (sl.monster && sl.bonus) {
    const v = preyValue(sl.bonus.type, sl.bonus.grade);
    const left = sl.left ?? 0;
    const frac = Math.max(0, Math.min(1, left / PREY_DURATION_S));
    const state = left <= 0 ? ' out' : left < 15 * 60 ? ' low' : '';
    const o = props.outfitOf(sl.monster);
    const noCard = props.st.cards < PREY_CARD_BONUS;
    return (
      <div class={'py-card py-active py-b-' + sl.bonus.type + state}>
        <div class="py-card-top"><span class="py-num">{ROMAN[props.i]}</span><b title={sl.monster}>{sl.monster}</b></div>
        <div class="py-trophy">
          <span class="py-scratch"><i /><i /><i /></span>
          <span class="py-face">{o ? <Portrait outfit={o} size={64} /> : <i>?</i>}</span>
          <img class="py-bonus-img" src={`${IMG}/prey-bonus-${BONUS_IMG[sl.bonus.type]}.png`} alt="" title={PREY_BONUS_NAMES[sl.bonus.type]} />
        </div>
        <div class="py-stars" title={`Grau ${sl.bonus.grade} de 10`}>{Array.from({ length: 10 }, (_, k) => <i key={k} class={k < sl.bonus!.grade ? 'on' : ''} />)}</div>
        <div class="py-bonus">
          <small>{PREY_BONUS_NAMES[sl.bonus.type]}</small>
          <b>+{v}%</b>
          <em>grau {sl.bonus.grade}/10 · máx. +{preyValue(sl.bonus.type, 10)}%</em>
        </div>
        <div class="py-time" title="Tempo de caça restante">
          <i style={{ width: `${frac * 100}%` }} />
          <span>{left <= 0 ? 'Tempo esgotado' : `${hm(left)} de caça`}</span>
        </div>
        <div class="py-btns">
          <button class="py-imgbtn" disabled={noCard} title={noCard ? 'Você precisa de 1 prey card' : 'Sortear outro bônus (1 prey card); o tempo volta para 2 horas'}
            onClick={() => props.send('bonus', props.i)}><img src={`${IMG}/prey-bonus-reroll${noCard ? '-disabled' : ''}.png`} alt="" /><small>Outro bônus<br />1 card</small></button>
          <button class="tb-button py-cancel" onClick={() => props.send('cancel', props.i)}>Cancelar prey</button>
        </div>
      </div>
    );
  }

  const free = Date.now() >= sl.freeAt;
  const noPick = props.st.cards < PREY_CARD_PICK;
  const anyOk = props.all.includes(anyName) && !used.includes(anyName);
  return (
    <div class={'py-card py-choose' + (sel ? ' picked' : '')}>
      <div class="py-card-top"><span class="py-num">{ROMAN[props.i]}</span><b>{any ? 'Qualquer criatura' : sel ?? 'Escolha a presa'}</b></div>
      {!any ? (
        <div class="py-list">
          {sl.list.map((n) => {
            const o = props.outfitOf(n);
            const u = used.includes(n);
            return (
              <button key={n} class={'py-mon' + (sel === n ? ' on' : '') + (u ? ' used' : '')} title={u ? `${n} (já está em outro espaço)` : n} disabled={u} onClick={() => setSel(n)}>
                <span>{o ? <Portrait outfit={o} size={36} /> : <i>?</i>}</span>
                <small>{n}</small>
              </button>
            );
          })}
        </div>
      ) : (
        <div class="py-any">
          <img src={`${IMG}/taskboard/icon-arbitrarymonster64x64.png`} alt="" class={props.outfitOf(anyName) ? 'py-hide' : ''} />
          {props.outfitOf(anyName) && <span class="py-face"><Portrait outfit={props.outfitOf(anyName)!} size={48} /></span>}
          <p>Escolha qualquer criatura das hunts por <b>{PREY_CARD_PICK} prey cards</b>:</p>
          <input list={'prey-all-' + props.i} value={anyName} placeholder="Nome da criatura" onInput={(e) => setAnyName((e.target as HTMLInputElement).value)} />
          <datalist id={'prey-all-' + props.i}>{props.all.map((n) => <option key={n} value={n} />)}</datalist>
          <small class={'py-why' + (anyOk ? ' ok' : '')}>
            {!anyName ? 'Digite o nome para buscar.' : used.includes(anyName) ? 'Essa criatura já está em outro espaço.' : !props.all.includes(anyName) ? 'Criatura não encontrada nas hunts.' : 'Pronto para escolher.'}
          </small>
        </div>
      )}
      <div class="py-btns">
        {!any ? <>
          <button class="py-imgbtn" title={free ? 'Lista nova (grátis)' : `Lista nova por ${props.rerollGold.toLocaleString('pt-BR')} gp (grátis em ${hm(Math.ceil((sl.freeAt - Date.now()) / 1000))})`}
            onClick={() => { setSel(null); props.send('list', props.i); }}>
            <img src={`${IMG}/prey-list-reroll.png`} alt="" /><small>{free ? <>Lista nova<br /><b class="py-free">grátis</b></> : <>Lista nova<br />{props.rerollGold.toLocaleString('pt-BR')} gp</>}</small>
          </button>
          <button class="py-imgbtn" disabled={!sel} title={sel ? `Confirmar ${sel}` : 'Clique numa criatura primeiro'} onClick={() => sel && props.send('pick', props.i, sel)}>
            <img src={`${IMG}/prey-confirm-monster-selection${sel ? '' : '-disabled'}.png`} alt="" /><small>Confirmar<br />{sel ? 'presa' : '—'}</small>
          </button>
          <button class="tb-button py-anybtn" disabled={noPick} title={noPick ? `Precisa de ${PREY_CARD_PICK} prey cards` : 'Escolher qualquer criatura'} onClick={() => setAny(true)}>Qualquer criatura ({PREY_CARD_PICK} cards)</button>
        </> : <>
          <button class="tb-button green" disabled={!anyOk} onClick={() => { props.send('any', props.i, anyName); setAny(false); }}>Escolher</button>
          <button class="tb-button" onClick={() => setAny(false)}>Voltar</button>
        </>}
      </div>
    </div>
  );
}
